import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Header from '../components/Header';

const STAGES = [
  { key: 'planning', label: 'Planning' },
  { key: 'acquisition', label: 'Acquisition' },
  { key: 'execution', label: 'Execution' },
  { key: 'complete', label: 'Complete' }
];

const PRIORITY_COLOR = { low: '#9e9e9e', medium: '#f57c00', high: '#d32f2f' };

const emptyForm = {
  orgName: '', orgType: 'library', contactName: '', contactEmail: '',
  region: '', priority: 'medium', notes: '', nextActionDate: '', eventDate: ''
};

const Partnerships = () => {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      window.location.href = '/signin?redirect=/partnerships';
      return;
    }
    fetchPartnerships();
  }, []);

  const fetchPartnerships = async () => {
    try {
      const response = await fetch('/api/partnerships');
      const data = await response.json();
      if (data.success) {
        setPartnerships(data.partnerships || []);
      }
    } catch (err) {
      console.error('Error fetching partnerships:', err);
    } finally {
      setLoading(false);
    }
  };

  const moveStage = async (id, newStage) => {
    try {
      const response = await fetch(`/api/partnerships/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: newStage })
      });
      const data = await response.json();
      if (data.success) {
        setPartnerships((prev) =>
          prev.map((p) => (p.id === id ? data.partnership : p))
        );
      }
    } catch (err) {
      console.error('Error updating stage:', err);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch('/api/partnerships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await response.json();
      if (data.success) {
        setPartnerships((prev) => [data.partnership, ...prev]);
        setForm(emptyForm);
        setDialogOpen(false);
      }
    } catch (err) {
      console.error('Error creating partnership:', err);
    }
  };

  const nextStage = (current) => {
    const idx = STAGES.findIndex((s) => s.key === current);
    return idx >= 0 && idx < STAGES.length - 1 ? STAGES[idx + 1].key : null;
  };

  return (
    <Box>
      <Header cartQty={0} />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Partnerships</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Partnership
          </Button>
        </Box>

        <Grid container spacing={2}>
          {STAGES.map((stage) => (
            <Grid item xs={12} md={3} key={stage.key}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {stage.label} ({partnerships.filter((p) => p.stage === stage.key).length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 200 }}>
                {partnerships
                  .filter((p) => p.stage === stage.key)
                  .map((p) => (
                    <Card key={p.id} variant="outlined">
                      <CardContent>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{p.orgName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {p.contactName || 'No contact yet'}
                        </Typography>
                        <Chip
                          label={p.priority}
                          size="small"
                          sx={{ mt: 1, backgroundColor: PRIORITY_COLOR[p.priority] || '#9e9e9e', color: '#fff' }}
                        />
                        {p.nextActionDate && (
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Next action: {new Date(p.nextActionDate).toLocaleDateString()}
                          </Typography>
                        )}
                        {nextStage(p.stage) && (
                          <Button
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() => moveStage(p.id, nextStage(p.stage))}
                          >
                            Move to {STAGES.find((s) => s.key === nextStage(p.stage)).label} →
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Partnership</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Organization name"
            value={form.orgName}
            onChange={(e) => setForm({ ...form, orgName: e.target.value })}
            required
            fullWidth
          />
          <TextField
            select
            label="Type"
            value={form.orgType}
            onChange={(e) => setForm({ ...form, orgType: e.target.value })}
            fullWidth
          >
            <MenuItem value="library">Library</MenuItem>
            <MenuItem value="school">School</MenuItem>
            <MenuItem value="museum">Museum</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            label="Contact name"
            value={form.contactName}
            onChange={(e) => setForm({ ...form, contactName: e.target.value })}
            fullWidth
          />
          <TextField
            label="Contact email"
            value={form.contactEmail}
            onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
            fullWidth
          />
          <TextField
            label="Region"
            value={form.region}
            onChange={(e) => setForm({ ...form, region: e.target.value })}
            fullWidth
          />
          <TextField
            select
            label="Priority"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            fullWidth
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </TextField>
          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.orgName}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Partnerships;